import React, { useCallback, useState } from "react";
import { adminApi } from "../services/adminApi";
import usePolling from "../hooks/usePolling";
import Pagination from "../components/Pagination";

export default function Operations() {
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [compConfig, setCompConfig] = useState(null);
  const [compSummary, setCompSummary] = useState(null);
  const [compLedger, setCompLedger] = useState([]);
  const [ledgerPaging, setLedgerPaging] = useState({ page: 1, pageSize: 10, total: 0 });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [summaryRes, settingsRes, compConfigRes, compSummaryRes, compLedgerRes] =
        await Promise.all([
        adminApi.getOperationsSummary(),
        adminApi.getSettings(),
        adminApi.getCompensationConfig(),
        adminApi.getCompensationSummary(),
        adminApi.getCompensationLedger({
          page: ledgerPaging.page,
          pageSize: ledgerPaging.pageSize,
        }),
      ]);
      setSummary(summaryRes);
      setSettings(settingsRes.settings || {});
      setCompConfig(compConfigRes.config || null);
      setCompSummary(compSummaryRes || null);
      setCompLedger(compLedgerRes.items || []);
      setLedgerPaging((prev) => ({ ...prev, total: compLedgerRes.total || 0 }));
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load operations data");
    }
  }, [ledgerPaging.page, ledgerPaging.pageSize]);

  usePolling(load, 30000, [ledgerPaging.page, ledgerPaging.pageSize]);

  const updateSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await adminApi.updateSettings(settings);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const updateCompensation = async () => {
    if (!compConfig) return;
    setSaving(true);
    try {
      await adminApi.updateCompensationConfig(compConfig);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update compensation config");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2>Operations & Settings</h2>
      {error ? <div className="error">{error}</div> : null}

      {summary ? (
        <div className="row">
          <div className="card">Pending withdrawals: {summary.pendingWithdrawals}</div>
          <div className="card">Failed withdrawals (24h): {summary.failedWithdrawals24h}</div>
          <div className="card">Active stakes: {summary.activeStakes}</div>
          <div className="card">Active revenue bonuses: {summary.activeRevenueBonuses}</div>
        </div>
      ) : null}

      {settings ? (
        <div className="card" style={{ marginTop: 14, maxWidth: 600 }}>
          <h3>Admin Settings</h3>
          <div className="row">
            <label>
              <input
                type="checkbox"
                checked={!!settings.maintenanceMode}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, maintenanceMode: e.target.checked }))
                }
              />{" "}
              Maintenance Mode
            </label>
            <label>
              <input
                type="checkbox"
                checked={!!settings.withdrawalsPaused}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, withdrawalsPaused: e.target.checked }))
                }
              />{" "}
              Pause Withdrawals
            </label>
            <label>
              <input
                type="checkbox"
                checked={settings.requireWithdrawalApproval === true}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    requireWithdrawalApproval: e.target.checked,
                  }))
                }
              />{" "}
              Require Admin Approval For Withdrawals
            </label>
            <label>
              <input
                type="checkbox"
                checked={!!settings.depositsPaused}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, depositsPaused: e.target.checked }))
                }
              />{" "}
              Pause Deposits
            </label>
          </div>
          <div style={{ marginTop: 10 }}>
            <button className="btn-primary" onClick={updateSettings} disabled={saving}>
              {saving ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </div>
      ) : null}

      {compSummary ? (
        <div className="card" style={{ marginTop: 14 }}>
          <h3>Compensation Summary</h3>
          <div className="row">
            <div className="card">
              Working: {compSummary.working?.totalNet?.toFixed?.(2) || 0}
            </div>
            <div className="card">
              Non-working: {compSummary.nonWorking?.totalNet?.toFixed?.(2) || 0}
            </div>
            <div className="card">Cap Applied Entries: {compSummary.capAppliedCount || 0}</div>
          </div>
        </div>
      ) : null}

      {compConfig ? (
        <div className="card" style={{ marginTop: 14 }}>
          <h3>Compensation Plan Config</h3>
          <div className="row">
            <label>
              Working Cap Multiplier
              <input
                type="number"
                value={compConfig.globalCaps?.workingMultiplier ?? 4}
                onChange={(e) =>
                  setCompConfig((prev) => ({
                    ...prev,
                    globalCaps: {
                      ...(prev.globalCaps || {}),
                      workingMultiplier: Number(e.target.value),
                    },
                  }))
                }
              />
            </label>
            <label>
              Non-Working Cap Multiplier
              <input
                type="number"
                value={compConfig.globalCaps?.nonWorkingMultiplier ?? 3}
                onChange={(e) =>
                  setCompConfig((prev) => ({
                    ...prev,
                    globalCaps: {
                      ...(prev.globalCaps || {}),
                      nonWorkingMultiplier: Number(e.target.value),
                    },
                  }))
                }
              />
            </label>
            <label>
              Arbitrage Min %
              <input
                type="number"
                value={compConfig.arbitrage?.minMonthlyPercent ?? 5}
                onChange={(e) =>
                  setCompConfig((prev) => ({
                    ...prev,
                    arbitrage: {
                      ...(prev.arbitrage || {}),
                      minMonthlyPercent: Number(e.target.value),
                    },
                  }))
                }
              />
            </label>
            <label>
              Minimum arbitrage investment (USDT)
              <input
                type="number"
                min={0}
                step="1"
                value={compConfig.arbitrage?.minPrincipal ?? 100}
                onChange={(e) =>
                  setCompConfig((prev) => ({
                    ...prev,
                    arbitrage: {
                      ...(prev.arbitrage || {}),
                      minPrincipal: Number(e.target.value),
                    },
                  }))
                }
              />
            </label>
            <label>
              Arbitrage Max %
              <input
                type="number"
                value={compConfig.arbitrage?.maxMonthlyPercent ?? 12}
                onChange={(e) =>
                  setCompConfig((prev) => ({
                    ...prev,
                    arbitrage: {
                      ...(prev.arbitrage || {}),
                      maxMonthlyPercent: Number(e.target.value),
                    },
                  }))
                }
              />
            </label>
            <label>
              Max arbitrage trades per user per UTC day
              <input
                type="number"
                min={1}
                max={100}
                value={compConfig.arbitrage?.maxTradesPerDay ?? 1}
                onChange={(e) =>
                  setCompConfig((prev) => ({
                    ...prev,
                    arbitrage: {
                      ...(prev.arbitrage || {}),
                      maxTradesPerDay: Number(e.target.value),
                    },
                  }))
                }
              />
            </label>
            <label>
              Bot monthly profit % (admin target)
              <input
                type="number"
                value={compConfig.botProfit?.monthlyPercent ?? 8}
                onChange={(e) =>
                  setCompConfig((prev) => ({
                    ...prev,
                    botProfit: {
                      ...(prev.botProfit || {}),
                      monthlyPercent: Number(e.target.value),
                      distributionDays: prev.botProfit?.distributionDays ?? 30,
                    },
                  }))
                }
              />
            </label>
            <label>
              Distribution days (e.g. 30)
              <input
                type="number"
                value={compConfig.botProfit?.distributionDays ?? 30}
                onChange={(e) =>
                  setCompConfig((prev) => ({
                    ...prev,
                    botProfit: {
                      ...(prev.botProfit || {}),
                      monthlyPercent: prev.botProfit?.monthlyPercent ?? 8,
                      distributionDays: Number(e.target.value),
                    },
                  }))
                }
              />
            </label>
            <p className="text-sm opacity-80" style={{ gridColumn: "1 / -1" }}>
              Daily accrual ≈{" "}
              {(
                Number(compConfig.botProfit?.monthlyPercent ?? 8) /
                Math.max(1, Number(compConfig.botProfit?.distributionDays ?? 30))
              ).toFixed(4)}
              % per day (monthly % ÷ days).
            </p>
          </div>
          <div style={{ marginTop: 10 }}>
            <button className="btn-primary" onClick={updateCompensation} disabled={saving}>
              {saving ? "Saving..." : "Save Compensation Config"}
            </button>
          </div>
        </div>
      ) : null}

      {compLedger.length ? (
        <div className="card" style={{ marginTop: 14 }}>
          <h3>Recent Compensation Ledger</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Source</th>
                  <th>Type</th>
                  <th>Gross</th>
                  <th>Net</th>
                  <th>Cap</th>
                </tr>
              </thead>
              <tbody>
                {compLedger.map((item) => (
                  <tr key={item.id}>
                    <td>{new Date(item.createdAt).toLocaleString()}</td>
                    <td>{item.user?.email || item.userId}</td>
                    <td>{item.source}</td>
                    <td>{item.classification}</td>
                    <td>{item.grossAmount}</td>
                    <td>{item.netAmount}</td>
                    <td>{item.capApplied ? item.capReason || "yes" : "no"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={ledgerPaging.page}
            pageSize={ledgerPaging.pageSize}
            total={ledgerPaging.total}
            onPageChange={(nextPage) =>
              setLedgerPaging((prev) => ({
                ...prev,
                page: nextPage,
              }))
            }
          />
        </div>
      ) : null}
    </div>
  );
}
